<script lang="ts">
  import { showAlert } from '../lib/ui-alert';
  import Icon from './Icon.svelte';
  import type { IconName } from '../lib/icons';
  import { onMount, onDestroy, untrack } from 'svelte';
  import {
    fetchExportJobs,
    createExportJob,
    deleteExportJob,
    exportJobDownloadUrl,
    type ApiExportJob,
  } from '../lib/data-tables-api';

  let {
    tableSlug,
    label,
    totalCount = null,
    params = {},
    offerAllFields = false,
  }: {
    tableSlug: string;
    label: string;
    totalCount?: number | null;
    params?: Record<string, unknown>;
    /** Показати перемикач «Усі доступні поля» у модалці підтвердження. */
    offerAllFields?: boolean;
  } = $props();

  /** Експортувати всі поля таблиці, а не лише видимі колонки. */
  let allFields = $state(false);

  let open = $state(false);
  let jobs = $state<ApiExportJob[]>([]);
  let creating = $state(false);
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  /** Модалка підтвердження перед створенням задачі */
  let confirmOpen = $state(false);

  const LS_KEY = `export-downloaded`;

  function loadDownloaded(): Set<string> {
    try {
      const raw = localStorage.getItem(LS_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  }

  function saveDownloaded(ids: Set<string>) {
    try { localStorage.setItem(LS_KEY, JSON.stringify([...ids])); } catch {}
  }

  /** IDs файлів які вже скачали (персистується в localStorage) */
  let downloadedIds = $state(loadDownloaded());

  $effect(() => {
    // Реагуємо на зміни downloadedIds і зберігаємо у localStorage
    const ids = downloadedIds;
    untrack(() => saveDownloaded(ids));
  });
  /** ID запису що очікує підтвердження видалення */
  let deleteConfirmId = $state<string | null>(null);

  const pendingCount = $derived(jobs.filter((j) => j.status === 'pending' || j.status === 'processing').length);
  /** Зелена крапка — є готові файли які ще не скачували */
  const undownloadedDoneCount = $derived(jobs.filter((j) => j.status === 'done' && !downloadedIds.has(j.id)).length);

  async function loadJobs() {
    try {
      const r = await fetchExportJobs();
      jobs = r.items.filter((j) => j.tableSlug === tableSlug);
    } catch {}
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(async () => {
      await loadJobs();
      if (pendingCount === 0) stopPolling();
    }, 4000);
  }

  function stopPolling() {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  }

  onMount(loadJobs);
  onDestroy(stopPolling);

  /** Відкриває модалку підтвердження */
  function openConfirm() {
    confirmOpen = true;
    open = false;
  }

  /** Підтверджено — ставимо задачу в чергу */
  async function onConfirm() {
    confirmOpen = false;
    creating = true;
    try {
      const finalParams = offerAllFields ? { ...params, allFields } : params;
      const job = await createExportJob({ tableSlug, label, params: finalParams });
      jobs = [job, ...jobs];
      open = true;
      startPolling();
    } catch (e) {
      showAlert((e as Error).message);
    } finally {
      creating = false;
    }
  }

  async function onDelete(id: string) {
    deleteConfirmId = null;
    try {
      await deleteExportJob(id);
      jobs = jobs.filter((j) => j.id !== id);
      downloadedIds = new Set([...downloadedIds].filter((x) => x !== id));
    } catch {}
  }

  async function download(id: string) {
    try {
      const resp = await fetch(exportJobDownloadUrl(id), { credentials: 'include' });
      if (!resp.ok) throw new Error(`Помилка ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export-${tableSlug}-${id.slice(0, 8)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Позначаємо як скачаний — зелена крапка зникне якщо більше немає нових
      downloadedIds = new Set([...downloadedIds, id]);
    } catch (e) {
      showAlert((e as Error).message);
    }
  }

  function statusIcon(status: string): IconName {
    if (status === 'pending')    return 'statusPending';
    if (status === 'processing') return 'statusProcessing';
    if (status === 'done')       return 'statusDone';
    return 'statusFail';
  }

  function fmtDate(iso: string): string {
    return new Date(iso).toLocaleString('uk-UA', { dateStyle: 'short', timeStyle: 'short' });
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') { open = false; confirmOpen = false; }
  }
</script>

<svelte:window onkeydown={onKeydown} />

<!-- Confirm modal -->
{#if confirmOpen}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px]"
    role="presentation"
    onclick={() => (confirmOpen = false)}
  >
    <div
      class="w-[340px] rounded-2xl border border-[var(--color-border-3)] bg-white shadow-2xl overflow-hidden"
      role="dialog"
      onclick={(e) => e.stopPropagation()}
    >
      <div class="px-5 py-4 border-b border-[var(--color-border-3)] flex items-center justify-between">
        <span class="text-sm font-semibold">Підготувати CSV</span>
        <button type="button" onclick={() => (confirmOpen = false)} class="inline-flex items-center text-[var(--color-text-muted)] hover:text-[var(--color-text)]"><Icon name="close" size={16} /></button>
      </div>
      <div class="px-5 py-4 space-y-3">
        {#if totalCount != null}
          <p class="text-sm text-[var(--color-text)]">
            Буде завантажено <strong>{totalCount.toLocaleString('uk-UA')}</strong> записів з поточними фільтрами.
          </p>
        {:else}
          <p class="text-sm text-[var(--color-text)]">
            Файл буде підготовлено у фоні.
          </p>
        {/if}
        {#if offerAllFields}
          <label class="flex items-start gap-2 rounded-lg border border-[var(--color-border-3)] bg-[var(--color-bg-3)]/50 px-3 py-2 cursor-pointer">
            <input type="checkbox" bind:checked={allFields} class="mt-0.5 accent-[var(--color-pu)]" />
            <span class="text-[12px] leading-snug">
              <span class="font-semibold">Усі доступні поля</span>
              <span class="block text-[10.5px] text-[var(--color-text-muted-2)]">
                Експортувати всі поля платежу (метод оплати, картка, платник, комісія…), а не лише видимі колонки.
              </span>
            </span>
          </label>
        {/if}
        <p class="text-[11px] text-[var(--color-text-muted-2)]">
          Коли буде готово — завантажте через <strong>Історію експортів</strong>.
        </p>
        <div class="flex gap-2 pt-1">
          <button
            type="button"
            onclick={onConfirm}
            class="flex-1 rounded-xl bg-[var(--color-pu)] text-white py-2 text-[12px] font-semibold hover:opacity-90"
          >Експортувати</button>
          <button
            type="button"
            onclick={() => (confirmOpen = false)}
            class="rounded-xl border border-[var(--color-border-3)] px-4 py-2 text-[12px] hover:bg-[var(--color-bg-3)]"
          >Скасувати</button>
        </div>
      </div>
    </div>
  </div>
{/if}

<div class="relative">
  <!-- Button group -->
  <div class="flex items-stretch">
    <!-- Main CSV button -->
    <button
      type="button"
      onclick={openConfirm}
      disabled={creating}
      class="inline-flex items-center gap-1.5 rounded-l-md border border-r-0 border-[var(--color-border-3)] bg-white px-3 py-1.5 text-[11px] font-semibold text-[var(--color-text)] hover:bg-[var(--color-bg-3)] disabled:opacity-60"
    >
      {#if creating}…{:else}<Icon name="download" size={14} />{/if} CSV
    </button>
    <!-- History toggle -->
    <button
      type="button"
      onclick={() => { open = !open; if (open) void loadJobs(); }}
      class="relative inline-flex items-center rounded-r-md border border-[var(--color-border-3)] bg-white px-2 py-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-3)] hover:text-[var(--color-text)]"
      title="Історія експортів"
    >
      <Icon name="chevronDown" size={14} />
      {#if pendingCount > 0}
        <span class="absolute -top-1.5 -right-1.5 grid h-4 w-4 place-items-center rounded-full bg-amber-400 text-[8px] font-bold text-white leading-none">{pendingCount}</span>
      {:else if undownloadedDoneCount > 0}
        <span class="absolute -top-1.5 -right-1.5 h-2.5 w-2.5 rounded-full bg-emerald-500 border-2 border-white"></span>
      {/if}
    </button>
  </div>

  {#if open}
    <div class="fixed inset-0 z-30" role="presentation" onclick={() => (open = false)}></div>

    <div class="absolute right-0 top-full mt-1.5 z-40 w-80 rounded-xl border border-[var(--color-border-3)] bg-white shadow-xl overflow-hidden">
      <div class="px-3.5 py-2.5 border-b border-[var(--color-border-3)] flex items-center justify-between">
        <span class="text-[10.5px] font-bold uppercase tracking-wider text-[var(--color-text-muted-2)]">Історія експортів</span>
        <button
          type="button"
          onclick={openConfirm}
          class="text-[10.5px] font-semibold text-[var(--color-pu-3)] hover:underline"
        >+ Новий</button>
      </div>

      {#if jobs.length === 0}
        <div class="px-4 py-5 text-center text-xs text-[var(--color-text-muted-2)] italic">
          Ще немає експортів.<br />Натисніть «CSV» щоб створити задачу.
        </div>
      {:else}
        <div class="max-h-72 overflow-y-auto divide-y divide-[var(--color-border-3)]">
          {#each jobs as j (j.id)}
            {@const isDownloaded = downloadedIds.has(j.id)}
            <div class="px-3.5 py-2.5 flex items-start gap-2 hover:bg-[var(--color-bg-3)]/50 group">
              <span class="mt-0.5 shrink-0 inline-flex"><Icon name={statusIcon(j.status)} size={15} /></span>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1.5">
                  <span class="text-[11.5px] font-medium truncate">{j.label}</span>
                  {#if isDownloaded}
                    <span class="shrink-0 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-semibold px-1.5 py-0.5 leading-none">скачано</span>
                  {/if}
                </div>
                <div class="text-[10px] text-[var(--color-text-muted-2)]">
                  {fmtDate(j.createdAt)}
                  {#if j.recordCount != null} · {j.recordCount} рядків{/if}
                  {#if j.status === 'pending' || j.status === 'processing'} · обробляється…{/if}
                  {#if j.status === 'failed' && j.errorMessage}<br /><span class="text-red-600">{j.errorMessage}</span>{/if}
                </div>
              </div>

              {#if deleteConfirmId === j.id}
                <!-- Inline підтвердження видалення -->
                <div class="flex items-center gap-1 shrink-0">
                  <span class="text-[10px] text-[var(--color-text-muted)]">Видалити?</span>
                  <button
                    type="button"
                    onclick={() => onDelete(j.id)}
                    class="rounded px-2 py-0.5 bg-rose-500 text-white text-[10px] font-semibold hover:bg-rose-600"
                  >Так</button>
                  <button
                    type="button"
                    onclick={() => (deleteConfirmId = null)}
                    class="rounded px-2 py-0.5 border border-[var(--color-border-3)] text-[10px] hover:bg-[var(--color-bg-3)]"
                  >Ні</button>
                </div>
              {:else}
                <div class="flex items-center gap-1 shrink-0">
                  {#if j.status === 'done'}
                    <button
                      type="button"
                      onclick={() => download(j.id)}
                      class="rounded-md {isDownloaded ? 'border border-[var(--color-border-3)] text-[var(--color-text-muted)] bg-white' : 'bg-[var(--color-pu)] text-white'} px-2.5 py-1 text-[10.5px] font-semibold hover:opacity-80"
                    >{isDownloaded ? '↓ Ще раз' : '↓ Завантажити'}</button>
                  {/if}
                  <button
                    type="button"
                    onclick={() => (deleteConfirmId = j.id)}
                    class="text-[var(--color-text-muted-2)] hover:text-rose-500 text-sm opacity-0 group-hover:opacity-100 transition-opacity ml-1"
                    title="Видалити"
                  ><Icon name="close" size={14} /></button>
                </div>
              {/if}
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>
